/**
 * Files attached on the submit form (#1629).
 *
 * Upload happens as each file is picked, before the demand exists; the submit
 * call binds them by id. Downloads go through the API client because the file
 * URL needs the bearer token, which a plain link would not carry.
 */

import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'
import { blobErrorMessage } from '../lib/apiError'
import type { DemandAttachment } from '../types/demand'

export const MAX_DEMAND_ATTACHMENTS = 5

/** What the file picker offers. The server's allow-list is the authority. */
export const DEMAND_ATTACHMENT_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.csv,.txt'

export async function uploadDemandAttachment(file: File): Promise<DemandAttachment> {
  const form = new FormData()
  form.append('file', file)
  // No explicit Content-Type: the browser sets it with the multipart boundary.
  const { data } = await api.post<DemandAttachment>('/demand-attachments', form)
  return data
}

/** Starts the download. Resolves to null, or to the reason it failed — read
 *  out of the Blob, where a refusal arrives and apiErrorMessage cannot see it. */
export async function downloadDemandAttachment(file: DemandAttachment): Promise<string | null> {
  let data: Blob
  try {
    ;({ data } = await api.get<Blob>(`/demand-attachments/${file.id}`, { responseType: 'blob' }))
  } catch (err) {
    return blobErrorMessage(err, `Couldn't download ${file.filename}`)
  }
  const href = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = href
  a.download = file.filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoking in the same tick can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(href), 10_000)
  return null
}

/** A demand's files. Empty for a viewer who may not read them. */
export function useDemandAttachments(demandId: number | undefined) {
  return useQuery({
    queryKey: ['demands', demandId, 'attachments'],
    queryFn: async () => {
      const { data } = await api.get<DemandAttachment[]>(`/demands/${demandId}/attachments`)
      return data
    },
    enabled: demandId !== undefined,
  })
}
